import {
  Assessment as AnalyticsIcon,
  People as PeopleIcon,
  ShoppingCart as OrderIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import Currency from "../../components/common/Currency";
import { usePromotionCodeAnalyticsList } from "../../hooks/usePromotionCode";
import { formatDate } from "../../utils/formatters";
import AdminPage from "./AdminPage";

const PromotionCodeAnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const { data, isLoading, error } = usePromotionCodeAnalyticsList();

  const analyticsList = data?.data ?? [];

  // Calculate totals
  const totals = React.useMemo(() => {
    if (!analyticsList.length) {
      return {
        totalCodes: 0,
        activeCodes: 0,
        totalUses: 0,
        totalDiscountGiven: 0,
        totalRevenue: 0,
        uniqueUsers: 0,
      };
    }

    return {
      totalCodes: analyticsList.length,
      activeCodes: analyticsList.filter((a) => a.isActive).length,
      totalUses: analyticsList.reduce((sum, a) => sum + a.totalUses, 0),
      totalDiscountGiven: analyticsList.reduce(
        (sum, a) => sum + a.totalDiscountGiven,
        0
      ),
      totalRevenue: analyticsList.reduce(
        (sum, a) => sum + a.totalRevenueFromCodeUsers,
        0
      ),
      uniqueUsers: analyticsList.reduce((sum, a) => sum + a.uniqueUsers, 0),
    };
  }, [analyticsList]);

  if (isLoading) {
    return (
      <AdminPage>
        <Container maxWidth="xl">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        </Container>
      </AdminPage>
    );
  }

  if (error) {
    return (
      <AdminPage>
        <Container maxWidth="xl">
          <Alert severity="error">{t("admin.promotionCodeAnalytics.loadError")}</Alert>
        </Container>
      </AdminPage>
    );
  }

  return (
    <AdminPage>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            {t("admin.promotionCodeAnalytics.title")}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {t("admin.promotionCodeAnalytics.subtitle")}
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <AnalyticsIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t("admin.promotionCodeAnalytics.totalCodes")}</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {totals.totalCodes}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("admin.promotionCodeAnalytics.activeCount", { count: totals.activeCodes })}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t("admin.promotionCodeAnalytics.totalUses")}</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {totals.totalUses.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("admin.promotionCodeAnalytics.acrossAllCodes")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <PeopleIcon color="info" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t("admin.promotionCodeAnalytics.uniqueUsers")}</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  {totals.uniqueUsers.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("admin.promotionCodeAnalytics.usersWhoUsedCodes")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <MoneyIcon color="error" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t("admin.promotionCodeAnalytics.totalDiscount")}</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="error.main">
                  <Currency amount={totals.totalDiscountGiven} />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("admin.promotionCodeAnalytics.totalDiscountsGiven")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <OrderIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t("admin.promotionCodeAnalytics.totalRevenue")}</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  <Currency amount={totals.totalRevenue} />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("admin.promotionCodeAnalytics.fromOrdersWithCodes")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <CalendarIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="h6">{t("admin.promotionCodeAnalytics.avgOrderValue")}</Typography>
                </Box>
                <Typography variant="h4" fontWeight="bold">
                  <Currency
                    amount={
                      totals.totalUses > 0
                        ? totals.totalRevenue / totals.totalUses
                        : 0
                    }
                  />
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t("admin.promotionCodeAnalytics.averagePerOrder")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Analytics Table */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("admin.promotionCodeAnalytics.codePerformanceDetails")}
            </Typography>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("admin.promotionCodeAnalytics.columnCode")}</TableCell>
                    <TableCell>{t("admin.promotionCodeAnalytics.columnStatus")}</TableCell>
                    <TableCell align="right">{t("admin.promotionCodeAnalytics.columnTotalUses")}</TableCell>
                    <TableCell align="right">{t("admin.promotionCodeAnalytics.columnUniqueUsers")}</TableCell>
                    <TableCell align="right">{t("admin.promotionCodeAnalytics.columnToday")}</TableCell>
                    <TableCell align="right">{t("admin.promotionCodeAnalytics.columnThisWeek")}</TableCell>
                    <TableCell align="right">{t("admin.promotionCodeAnalytics.columnThisMonth")}</TableCell>
                    <TableCell align="right">{t("admin.promotionCodeAnalytics.columnTotalDiscount")}</TableCell>
                    <TableCell align="right">{t("admin.promotionCodeAnalytics.columnAvgDiscount")}</TableCell>
                    <TableCell align="right">{t("admin.promotionCodeAnalytics.columnTotalRevenue")}</TableCell>
                    <TableCell align="right">{t("admin.promotionCodeAnalytics.columnAvgOrderValue")}</TableCell>
                    <TableCell>{t("admin.promotionCodeAnalytics.columnExpires")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyticsList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        <Typography variant="body2" color="textSecondary" py={4}>
                          {t("admin.promotionCodeAnalytics.noCodesFound")}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    analyticsList.map((analytics) => (
                      <TableRow key={analytics.codeId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium" fontFamily="monospace">
                            {analytics.code}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {analytics.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={analytics.isActive ? t("admin.promotionCodeAnalytics.active") : t("admin.promotionCodeAnalytics.inactive")}
                            color={analytics.isActive ? "success" : "default"}
                            size="small"
                          />
                          {analytics.isExpired && (
                            <Chip
                              label={t("admin.promotionCodeAnalytics.expired")}
                              color="error"
                              size="small"
                              sx={{ ml: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {analytics.totalUses}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {analytics.uniqueUsers}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color={analytics.usesToday > 0 ? "success.main" : "textSecondary"}>
                            {analytics.usesToday}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color={analytics.usesThisWeek > 0 ? "success.main" : "textSecondary"}>
                            {analytics.usesThisWeek}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color={analytics.usesThisMonth > 0 ? "success.main" : "textSecondary"}>
                            {analytics.usesThisMonth}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="error.main" fontWeight="medium">
                            <Currency amount={analytics.totalDiscountGiven} />
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            <Currency
                              amount={analytics.averageDiscountPerOrder}
                            />
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="success.main" fontWeight="medium">
                            <Currency
                              amount={analytics.totalRevenueFromCodeUsers}
                            />
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            <Currency amount={analytics.averageOrderValue} />
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {analytics.expiresAt
                              ? formatDate(analytics.expiresAt)
                              : t("admin.promotionCodeAnalytics.never")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Container>
    </AdminPage>
  );
};

export default PromotionCodeAnalyticsPage;
